import { useState } from "react";
import { C, S, today, fmtDate } from "../../utils/theme";
import { saveData } from "../../utils/data";
import { Modal, Txtarea, Badge, PageHdr, Empty, StatCard } from "../../components/UI";

async function aiAnswer(question, courseName, apiKey) {
  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-api-key": apiKey, "anthropic-version": "2023-06-01", "anthropic-dangerous-direct-browser-access": "true" },
    body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 1000, system: `You are a knowledgeable university instructor for the course: "${courseName}". Give clear, concise academic answers to student questions. Use examples where helpful.`, messages: [{ role: "user", content: question }] }),
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error?.message || "API error"); }
  const d = await res.json();
  return d.content?.[0]?.text || "No answer generated.";
}

export default function AdminQA({ data, setData }) {
  const [replyModal, setReplyModal] = useState(null);
  const [reply, setReply] = useState("");
  const [apiKey, setApiKey] = useState(() => localStorage.getItem("uni_ai_key") || "");
const [keyModal, setKeyModal] = useState(false);
const [tempKey, setTempKey] = useState("");
  const [aiLoading, setAiLoading] = useState(null);
  const [aiError, setAiError] = useState("");

  const unanswered = data.qaThreads.filter(q => !q.answer);
  const answered = data.qaThreads.filter(q => q.answer);

  const submitReply = () => {
    if (!reply.trim()) return;
    const upd = { ...data, qaThreads: data.qaThreads.map(q => q.id === replyModal.id ? { ...q, answer: reply, aiAnswered: false, answeredAt: today() } : q) };
    setData(upd); saveData(upd); setReplyModal(null); setReply("");
  };

  const triggerAI = async (thread) => {
    if (!apiKey) { alert("No API key. Set it from Student Q&A page."); return; }
    const course = data.courses.find(c => c.id === thread.courseId);
    setAiLoading(thread.id); setAiError("");
    try {
      const answer = await aiAnswer(thread.question, course?.name || "General", apiKey);
      const upd = { ...data, qaThreads: data.qaThreads.map(q => q.id === thread.id ? { ...q, answer, aiAnswered: true, answeredAt: today() } : q) };
      setData(upd); saveData(upd);
    } catch (err) { setAiError(err.message); }
    finally { setAiLoading(null); }
  };

  const delReply = (id) => {
    const upd = { ...data, qaThreads: data.qaThreads.map(q => q.id === id ? { ...q, answer: null, aiAnswered: false, answeredAt: null } : q) };
    setData(upd); saveData(upd);
  };

  return (
    <div>
      <PageHdr title="Q&A Management" sub={`${unanswered.length} awaiting reply`}
  action={
    <button style={S.btn(apiKey ? "success" : "primary")} onClick={() => { setTempKey(apiKey); setKeyModal(true); }}>
      🤖 {apiKey ? "AI Key Set ✓" : "Set Claude AI Key"}
    </button>
  }
/>
      {aiError && <div style={{ ...S.alert("danger"), marginBottom: 16 }}>⚠️ {aiError}</div>}
      <div style={S.statsGrid}>
        <StatCard icon="⏳" label="Unanswered" value={unanswered.length} color="#F59E0B" />
        <StatCard icon="✅" label="Answered" value={answered.length} color="#10B981" />
        <StatCard icon="🤖" label="AI Answered" value={data.qaThreads.filter(q => q.aiAnswered).length} color="#3B82F6" />
        <StatCard icon="💬" label="Total" value={data.qaThreads.length} color="#8B5CF6" />
      </div>
      {unanswered.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#F59E0B", marginBottom: 14 }}>⏳ Unanswered ({unanswered.length})</h3>
          {unanswered.map(q => {
            const student = data.users.find(u => u.id === q.studentId);
            const course = data.courses.find(c => c.id === q.courseId);
            return (
              <div key={q.id} style={{ ...S.card, marginBottom: 12, borderLeft: "3px solid #F59E0B" }}>
                <div style={{ padding: "17px 22px", display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: C.white, marginBottom: 6 }}>"{q.question}"</div>
                    <div style={{ fontSize: 12, color: "#475569" }}>By <strong style={{ color: "#94A3B8" }}>{student?.name}</strong> • {course?.name} • {fmtDate(q.askedAt)}</div>
                    {aiLoading === q.id && <div style={{ marginTop: 10, fontSize: 13, color: "#3B82F6" }}>🤖 AI is thinking...</div>}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                    {apiKey && <button style={S.btn("primary", "sm")} onClick={() => triggerAI(q)} disabled={aiLoading === q.id}>🤖 AI Reply</button>}
                    <button style={S.btn("success", "sm")} onClick={() => { setReplyModal(q); setReply(""); }}>✍️ Manual</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: "#10B981", marginBottom: 14 }}>✅ Answered ({answered.length})</h3>
        {answered.length === 0 ? <div style={{ color: "#475569", fontSize: 14 }}>No answered questions yet.</div>
          : answered.map(q => {
            const student = data.users.find(u => u.id === q.studentId);
            return (
              <div key={q.id} style={{ ...S.card, marginBottom: 12, borderLeft: `3px solid ${q.aiAnswered ? "#3B82F6" : "#10B981"}` }}>
                <div style={{ padding: "17px 22px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: C.white, marginBottom: 4 }}>Q: {q.question}</div>
                      <div style={{ fontSize: 12, color: "#475569", marginBottom: 10 }}>By {student?.name}</div>
                      <div style={{ padding: "10px 14px", background: q.aiAnswered ? "rgba(59,130,246,0.08)" : "rgba(16,185,129,0.08)", borderRadius: 8, fontSize: 13, color: "#CBD5E1", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                        <span style={{ fontSize: 11, color: q.aiAnswered ? "#3B82F6" : "#10B981", fontWeight: 700, display: "block", marginBottom: 6 }}>{q.aiAnswered ? "🤖 AI Answer" : "✅ Manual Reply"}</span>
                        {q.answer}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginLeft: 12 }}>
                      <button style={S.btn("glass", "sm")} onClick={() => { setReplyModal(q); setReply(q.answer || ""); }}>✏️</button>
                      <button style={S.btn("danger", "sm")} onClick={() => delReply(q.id)}>🗑️</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        }
      </div>
      <Modal open={!!replyModal} onClose={() => setReplyModal(null)} title="Reply to Question"
        footer={<><button style={S.btn("ghost")} onClick={() => setReplyModal(null)}>Cancel</button><button style={S.btn("primary")} onClick={submitReply}>Save Reply</button></>}>
        {replyModal && <div style={S.alert("info")}><strong>Q:</strong> {replyModal.question}</div>}
        <Txtarea label="Your Answer" rows={5} value={reply} onChange={e => setReply(e.target.value)} />
      </Modal>
<Modal open={keyModal} onClose={() => setKeyModal(false)} title="🤖 Claude AI API Key"
  footer={<>
    <button style={S.btn("ghost")} onClick={() => setKeyModal(false)}>Cancel</button>
    <button style={S.btn("primary")} onClick={() => { localStorage.setItem("uni_ai_key", tempKey); setApiKey(tempKey); setKeyModal(false); }} disabled={!tempKey.trim()}>Save Key</button>
  </>}>
  <div style={S.alert("info")}>
    Admin yahan key set kare — sab students ko automatically AI answers milenge.
  </div>
  <div style={S.formGroup}>
    <label style={S.label}>Anthropic API Key</label>
    <input style={{ ...S.input, fontFamily: "monospace", fontSize: 13 }} type="password"
      placeholder="sk-ant-api03-..." value={tempKey} onChange={e => setTempKey(e.target.value)} />
    <div style={{ fontSize: 12, color: "#475569", marginTop: 6 }}>
      Get key from <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer" style={{ color: "#3B82F6" }}>console.anthropic.com</a>
    </div>
  </div>
  {apiKey && (
    <button style={S.btn("danger", "sm")} onClick={() => { localStorage.removeItem("uni_ai_key"); setApiKey(""); setTempKey(""); setKeyModal(false); }}>
      🗑️ Remove Key
    </button>
  )}
</Modal>

    </div>
  );
}
